import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { DayPicker } from "react-day-picker";
import { format, parse, isValid } from "date-fns";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

function Calendar({ className, classNames, showOutsideDays = true, month, onMonthChange, ...props }) {
  const calendarRef = React.useRef(null);
  const [displayMonth, setDisplayMonth] = React.useState(month || props.defaultMonth || new Date());

  // Track month changes
  const handleMonthChange = React.useCallback((newMonth) => {
    setDisplayMonth(newMonth);
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  }, [onMonthChange]);

  // Add data-testid to day buttons using MutationObserver for reliability
  React.useEffect(() => {
    if (!calendarRef.current) return;

    const applyTestIds = () => {
      if (!calendarRef.current) return;
      const buttons = calendarRef.current.querySelectorAll('button[name="day"]');
      buttons.forEach((button) => {
        const dayNumber = button.textContent.trim();
        // Skip if this is an "outside day" (from adjacent month)
        const isOutsideDay = button.classList.contains('day-outside');

        if (dayNumber && /^\d+$/.test(dayNumber) && !isOutsideDay) {
          try {
            const year = displayMonth.getFullYear();
            const monthNum = displayMonth.getMonth();
            const dayNum = parseInt(dayNumber, 10);
            const dateObj = new Date(year, monthNum, dayNum);

            if (isValid(dateObj)) {
              const testId = `calendar-day-${format(dateObj, 'yyyy-MM-dd')}`;
              button.setAttribute('data-testid', testId);
            }
          } catch (e) {
            // Skip if date construction fails
          }
        }
      });

      // Add testids to navigation buttons
      const prevButton = calendarRef.current.querySelector('button[class*="nav_button_previous"]');
      const nextButton = calendarRef.current.querySelector('button[class*="nav_button_next"]');
      if (prevButton) prevButton.setAttribute('data-testid', 'calendar-prev-btn');
      if (nextButton) nextButton.setAttribute('data-testid', 'calendar-next-btn');
    };

    // Apply immediately
    applyTestIds();

    // Observe DOM changes to reapply after React re-renders
    const observer = new MutationObserver(() => {
      applyTestIds();
    });
    observer.observe(calendarRef.current, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [displayMonth]);

  return (
    <div ref={calendarRef}>
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn("p-3", className)}
        month={displayMonth}
        onMonthChange={handleMonthChange}
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption: "flex justify-center pt-1 relative items-center",
          caption_label: "text-sm font-medium",
          nav: "space-x-1 flex items-center",
          nav_button: cn(
            buttonVariants({ variant: "outline" }),
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          ),
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse space-y-1",
          head_row: "flex",
          head_cell:
            "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
          row: "flex w-full mt-2",
          cell: cn(
            "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
            props.mode === "range"
              ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
              : "[&:has([aria-selected])]:rounded-md"
          ),
          day: cn(
            buttonVariants({ variant: "ghost" }),
            "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
          ),
          day_range_start: "day-range-start",
          day_range_end: "day-range-end",
          day_selected:
            "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
          day_today: "bg-accent text-accent-foreground",
          day_outside:
            "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
          day_disabled: "text-muted-foreground opacity-50",
          day_range_middle:
            "aria-selected:bg-accent aria-selected:text-accent-foreground",
          day_hidden: "invisible",
          ...classNames,
        }}
        components={{
          IconLeft: ({ ...iconProps }) => (
            <ChevronLeft className="h-4 w-4" data-testid="calendar-prev-month" {...iconProps} />
          ),
          IconRight: ({ ...iconProps }) => (
            <ChevronRight className="h-4 w-4" data-testid="calendar-next-month" {...iconProps} />
          ),
        }}
        {...props}
      />
    </div>
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
